import { Component, ElementRef, ViewChild } from "@angular/core";
import _ from "lodash";
import { ActiveStatus, ActiveStatusEnum, Feature, FeatureKind } from "app/model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DomainService } from "app/shared/domain.service";
import { EntityFns } from "app/shared/entity-fns";
import { UtilFns } from "app/shared/util-fns";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

export interface EditFeatureDialogParams extends DialogParams {
  // empty item means to add one.
  feature?: Feature;
}

@Component({
  templateUrl: "./edit-feature-dialog.component.html",
  standalone: false,
})
export class EditFeatureDialogComponent extends DialogBaseComponent<EditFeatureDialogParams> {
  @ViewChild("firstInput") firstInput: ElementRef<HTMLInputElement>;

  params: EditFeatureDialogParams;
  feature: Feature;
  isAdding: boolean;

  activeStatuses: ActiveStatus[];
  featureKinds: FeatureKind[];

  /** Named so the message can say who is holding the kind in place. */
  lockedByManufacturers: string[] = [];

  constructor(
    public config: DynamicDialogConfig<EditFeatureDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService
  ) {
    super(config, dialogRef, domainService);

    this.prepare();
  }

  private async prepare() {
    this.feature = this.params.feature;
    if (this.feature == null) {
      this.isAdding = true;
      this.feature = this.createNewFeatureAsUnchanged();
    }

    const title = this.isAdding ? "Add Feature" : "Edit Feature";
    this.config.header = title;

    // Filter out the active statuses that are not relevant for features.
    this.activeStatuses = this.dbQueryService
      .getAllCached(ActiveStatus)
      .filter((x) => x.id === ActiveStatusEnum.Active || x.id === ActiveStatusEnum.Inactive);

    this.featureKinds = _.sortBy(this.dbQueryService.getAllCached(FeatureKind), (k) => k.name);

    // A kind a feed manufacturer requires is held in place. Read on open rather than computed
    // from a cache, because the requirement is edited on the manufacturer's own form and this
    // dialog must not offer a change that was forbidden a minute ago.
    const required = await this.dbQueryService.getFeatureKindsRequiredByFeedManufacturers();
    this.lockedByManufacturers = _.uniq(
      required
        .filter((m) => m.featureKindId === this.feature.featureKindId)
        .map((m) => m.manufacturer?.name)
        .filter((n) => !!n)
    ).sort();

    this.isPageReady = true;

    // Focus the first input after the view renders
    await UtilFns.wait(1);
    this.firstInput?.nativeElement?.focus();
  }

  private createNewFeatureAsUnchanged() {
    const feature = this.uow.createEntity(Feature, {
      mismatchAddonIsCrossAccount: false,
      activeStatusId: 1,
    });
    feature.entityAspect.setUnchanged();
    feature.entityAspect.validateEntity();
    return feature;
  }

  /**
   * Whether the feature type is held in place by a manufacturer that runs on a feed.
   *
   * Only ever true for a feature that already exists. A new one has no kind yet, so nothing can
   * be requiring it, and locking the field would leave the operator unable to finish the form.
   */
  get isFeatureKindLocked() {
    return !this.isAdding && this.lockedByManufacturers.length > 0;
  }

  get featureKindLockReason() {
    const names = this.lockedByManufacturers.join(', ');
    return `${names} requires this feature type on every product type.`
      + ` To change it, first remove the requirement from the manufacturer.`;
  }

  async ok() {
    const ok = await this.save();
    if (!ok) return;
    this.dialogRef.close(true);
  }

  async save() {
    if (this.isAdding) {
      this.feature.entityAspect.setAdded();
    }
    // remove excess spaces
    this.feature.name = this.feature.name?.trim();
    if (this.feature._origName == null) {
      this.feature._origName = this.feature.name;
    }

    const isValid = await this.validate();
    if (!isValid) {
      return false;
    }

    try {
      const sr = await this.dbSaveService.saveChanges();
      this.toastr.success("Feature updated");
      return true;
    } catch (e) {
      return false;
    }
  }

  async validate() {
    // Required, and checked here rather than left to the foreign key. FeatureKindId is a plain
    // int, so an unanswered dropdown reaches the server as 0 and comes back as a constraint
    // violation naming a column instead of a question naming a field.
    if (!this.feature.featureKindId) {
      await this.dialogService.showOkMessage(
        "Feature Type Required",
        "Select a feature type. The manufacturer feed uses it to put the correct features on a product type."
      );
      return false;
    }

    let matchingEntity = await this.dbQueryService.getMatchingEntity(Feature, this.feature, ["name"]);
    if (matchingEntity != null) {
      await this.dialogService.showOkMessage("Duplicate", `Cannot save because this feature is a duplicate of an existing feature. `);
      return false;
    }

    const ea = this.feature.entityAspect;
    if (!ea.validateEntity()) {
      const vErrors = ea.getValidationErrors();
      const errMsg = vErrors.map((ve) => ve.errorMessage).join("<br>");
      await this.dialogService.showOkMessage("Validation Error(s)", errMsg);
      return false;
    }
    return true;
  }

  async cancel() {
    // This insures that the rejectChanges below does not cause an html error on the page.
    this.isPageReady = false;
    if (this.isAdding) {
      this.feature.entityAspect.setDetached();
    } else {
      EntityFns.rejectOrDetach(this.feature.entityAspect);
    }

    this.dialogRef.close(false);
  }
}
